// appCatalog.js
//
// Single source of truth for the suite-level apps available in the dashboard
// and the admin assignment UI. Adding a new top-level app to the suite means
// appending one entry here.
//
// IMPORTANT NAMING NOTE:
// This is NOT the same as `applications` in Firestore. That collection holds
// product applications (e.g. "Web Portal", "Mobile App") that users file
// defects against. This file is about *suite apps* — the modules that make
// up the desk suite (Defect Tracker, Notes, SOP System, etc.).
//
// To avoid the naming collision, per-user permissions to suite apps use the
// field name `assignedSuiteApps` on the user doc, while the existing
// `assignedApps` field continues to mean "product applications visible to
// this customer."

export const SUITE_APPS = [
  {
    id: 'defect-tracker',
    name: 'Defect Tracker',
    icon: '🐛',
    description: 'Track and manage software defects',
    path: '/defect-tracker',
    active: true
  },
  {
    id: 'feature-requests',
    name: 'Feature Requests',
    icon: '✨',
    description: 'Request and vote on new features',
    path: '/feature-requests',
    active: true
  },
  {
    id: 'notes',
    name: 'Notes',
    icon: '📝',
    description: 'Create and share notes with your team',
    path: '/notes',
    active: true
  },
  {
    id: 'messaging',
    name: 'Messaging',
    icon: '💬',
    description: 'Chat with your team in real-time',
    path: '/messaging',
    active: true
  },
  {
    id: 'account-requests',
    name: 'Account Requests',
    icon: '📋',
    description: 'Manage new account requests',
    path: '/account-requests',
    active: true
  },
  {
    id: 'diagramming',
    name: 'Diagramming',
    icon: '📐',
    description: 'Create flowcharts and whiteboard diagrams',
    path: '/diagramming',
    active: true
  },
  {
    id: 'incident-reports',
    name: 'Incident Reports',
    icon: '🚨',
    description: 'Report and track incidents; auto-triggers related records',
    path: '/incident-reports',
    active: true
  },
  {
    id: 'injury-reports',
    name: 'Injury Reporting',
    icon: '🩹',
    description: 'Document workplace injuries and treatment',
    path: '/injury-reports',
    active: true
  },
  {
    id: 'sop-system',
    name: 'SOP System',
    icon: '📋',
    description: 'Standard Operating Procedures with version control',
    path: '/sop-system',
    active: true
  },
  {
    id: 'maintenance-requests',
    name: 'Maintenance Requests',
    icon: '🔧',
    description: 'Submit and track maintenance work orders',
    path: '/maintenance-requests',
    active: true
  }
];

// "Coming Soon" placeholders. Not part of the assignment system (you can't
// be granted access to a thing that doesn't exist). They render on the
// dashboard for visibility but never appear in the admin's assignment UI.
export const COMING_SOON_APPS = [
  {
    id: 'audit-trail',
    name: 'Audit Trail',
    icon: '📜',
    description: 'Track changes and view activity history across all applications',
    path: null,
    active: false
  },
  {
    id: 'project-management',
    name: 'Project Management',
    icon: '📋',
    description: 'Plan and track project progress',
    path: null,
    active: false
  },
  {
    id: 'crm',
    name: 'CRM',
    icon: '👥',
    description: 'Manage customer relationships',
    path: null,
    active: false
  },
  {
    id: 'invoicing',
    name: 'Invoicing',
    icon: '💰',
    description: 'Create and track invoices',
    path: null,
    active: false
  },
  {
    id: 'reporting',
    name: 'Reporting',
    icon: '📊',
    description: 'Generate insights and reports',
    path: null,
    active: false
  }
];

/** Returns the list of all active suite app IDs. */
export function getAllActiveAppIds() {
  return SUITE_APPS.filter(a => a.active).map(a => a.id);
}

/** Look up an app by its ID. Returns null if not found. */
export function getAppById(id) {
  return SUITE_APPS.find(a => a.id === id) ||
         COMING_SOON_APPS.find(a => a.id === id) ||
         null;
}

/**
 * Determine whether a user can access a given suite app.
 *
 * Build 25.1: two-tier check.
 *   1. The org must enable the app (org-wide setting).
 *   2. The user must be assigned the app (per-user setting).
 *
 * Both checks must pass. Admins are NOT bypassed here — admins are subject
 * to the same checks so they can curate their own dashboards. The /admin
 * route itself is always accessible to admins, but is enforced separately
 * (see AppAccessGate and Suite.js).
 *
 * @param userProfile   The user's profile object
 * @param appId         The app id to check
 * @param orgSettings   The org-wide settings object (must include enabledAppIds).
 *                      If null/undefined, falls back to allowing all apps —
 *                      this matters during the brief window before org settings
 *                      load for the first time.
 * @returns {boolean}
 */
export function userCanAccessApp(userProfile, appId, orgSettings) {
  if (!userProfile) return false;

  // Org gate. If we don't have org settings yet (still loading), don't
  // pretend the org has disabled everything — fall through.
  if (orgSettings && Array.isArray(orgSettings.enabledAppIds)) {
    if (!orgSettings.enabledAppIds.includes(appId)) return false;
  }

  // Per-user gate. Default DENY when no field set.
  const assigned = userProfile.assignedSuiteApps || [];
  return assigned.includes(appId);
}

/**
 * Is the app enabled at the org level? Used for things like the
 * AdminPanel's Suite Access UI: org-disabled apps should be shown as
 * disabled options, not just hidden.
 */
export function orgEnablesApp(orgSettings, appId) {
  if (!orgSettings || !Array.isArray(orgSettings.enabledAppIds)) return true;
  return orgSettings.enabledAppIds.includes(appId);
}
